import { Button, DatePicker, Form, Select, Table } from 'antd'
import { FileSearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { Field } from 'formik'
import { Link } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import startCase from 'lodash/startCase'
import api from '../../api'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import { getPaginationHeaders } from '../../redux/utils'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import datetime from '../../lib/datetime'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { useIsAdmin } from '../../redux/slices/session'

const { Item } = Form
const { Option } = Select
const { RangePicker } = DatePicker

const NOTIFICATION_SUBTYPE_OPTIONS = [
  { label: 'Confirmation of Connectivity', value: 'confirmation' },
  {
    label: 'Request for Updated Patient Information',
    value: 'contact_information',
  },
  { label: 'Inactive Insurance', value: 'insurance_information' },
  { label: 'Non-Compliant', value: 'non_compliant' },
  { label: 'ERI/EOL', value: 'eri_eol' },
  { label: 'Explanted', value: 'explanted' },
  { label: 'Transferred Care', value: 'transferred_care' },
  { label: 'Patient Deceased', value: 'patient_deceased' },
  { label: 'Other', value: 'other' },
]

const DEFAULT_FILTERS = {
  query: '',
  deviceModelManufacturer: FIELDS_OPTIONS.deviceModelManufacturer[0].id,
}

const QUERY_PARAMS_SCHEMA = {
  page: Number,
}

const useStyles = createUseStyles({
  filterItem: {
    minWidth: 200,
  },
})

export default function Notifications() {
  const classes = useStyles()
  const isAdmin = useIsAdmin()

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1, ...DEFAULT_FILTERS },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const fetchNotifications = useCallback(
    () => api.notifications.list(conditions),
    [conditions],
  )

  const { pending, error, clearError, value } = useAsync(fetchNotifications)

  const notifications = useMemo(() => (value ? value.body : []), [value])
  const { pageSize, total } = value ? getPaginationHeaders(value.headers) : {}

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const handleSubmit = useCallback(
    ({
      practiceId,
      deviceTypeId,
      deviceModelManufacturer,
      query,
      medicalProviderId,
      officeLocationId,
      subType,
    }) => {
      setConditions({
        ...conditions,
        page: 1,
        practiceId,
        deviceTypeId,
        deviceModelManufacturer,
        query,
        medicalProviderId,
        officeLocationId,
        subType,
      })
    },
    [conditions, setConditions],
  )

  const handleDateRangeChange = useCallback(
    dates => {
      if (!dates) {
        setConditions({
          ...conditions,
          fromDate: undefined,
          toDate: undefined,
          page: 1,
        })
        return
      }
      setConditions({
        ...conditions,
        fromDate: dates[0].format('YYYY-MM-DD'),
        toDate: dates[1].format('YYYY-MM-DD'),
        page: 1,
      })
    },
    [conditions, setConditions],
  )

  const dateRangeValue = useMemo(() => {
    if (!conditions.fromDate || !conditions.toDate) return undefined
    return [dayjs(conditions.fromDate), dayjs(conditions.toDate)]
  }, [conditions.fromDate, conditions.toDate])

  const subtypeFilter = (
    <Field name="subType">
      {({ field, form }) => (
        <Item className={classes.filterItem}>
          <Select
            allowClear
            placeholder="Notification type"
            style={{ width: '100%' }}
            value={field.value || undefined}
            onChange={val => form.setFieldValue('subType', val || null)}
          >
            {NOTIFICATION_SUBTYPE_OPTIONS.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Item>
      )}
    </Field>
  )

  const extraFilters = (
    <>
      {subtypeFilter}
      <Item className={classes.filterItem}>
        <RangePicker
          allowClear
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          ranges={{
            'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
            'Last Month': [
              dayjs().subtract(1, 'month').startOf('month'),
              dayjs().subtract(1, 'month').endOf('month'),
            ],
          }}
        />
      </Item>
    </>
  )

  const columns = [
    {
      title: 'Patient',
      key: 'patient',
      render(_, notification) {
        if (!notification.patient) return '-'
        return (
          <Link to={toPath.patients.patient({ id: notification.patient.id })}>
            {notification.patient.lastName}, {notification.patient.firstName}
          </Link>
        )
      },
    },
    {
      title: 'Type',
      dataIndex: ['notificationType'],
      key: 'notificationType',
      render: text => startCase(text),
    },
    {
      title: 'Sub-type',
      dataIndex: ['subType'],
      key: 'subType',
      render: text => startCase(text),
    },
    {
      title: 'Title',
      dataIndex: ['title'],
      key: 'title',
    },
    {
      title: 'Sent by',
      key: 'author',
      render(_, notification) {
        if (!notification.author) return '-'
        return startCase(
          [notification.author.firstName, notification.author.lastName]
            .filter(Boolean)
            .join(' '),
        )
      },
    },
    {
      title: 'Date',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      render(_, notification) {
        if (!notification.createdAt) return '-'
        return datetime(notification.createdAt).tz().calendar(null, {
          sameElse: 'MMM D, YYYY h:mm A',
        })
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render(_, notification) {
        return (
          <Link to={toPath.notifications.notification({ id: notification.id })}>
            <Button type="link" icon={<FileSearchOutlined />} />
          </Link>
        )
      },
    },
  ]

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader title="Notifications" />
      <Filters
        currentValues={conditions}
        onSubmit={handleSubmit}
        showPractice={isAdmin}
        showDeviceType
        showSearch
        showDeviceManufacturer
        showOfficeLocation
        showMedicalProvider
        extraFilter={extraFilters}
        placeholders={{
          query: 'Search by name, serial number or practice MRN',
        }}
      />
      <Table
        columns={columns}
        dataSource={notifications}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
    </div>
  )
}
